import React from 'react';
import { Link } from '@stashinvest/ui';

const PLACEHOLDER_PATTERN = /\{\{[^}]+\}\}/;
const PLACEHOLDER_REGEX = /^\{\{[^}]+\}\}$/;

type LinkConfig = {
    key: string;
    url: string;
    text: string;
};

type DynamicCopy = {
    text: string;
    interpolations: LinkConfig[];
};

type DynamicDisclosureProps = {
    dynamicCopy: DynamicCopy;
};

/**
 * Creates a map of link components grouped by their keys
 * @param interpolations - Array of link configurations
 * @returns Map of key to array of Link components
 */
const createLinkMap = (interpolations: LinkConfig[]): Record<string, JSX.Element[]> => {
    const linkMap: Record<string, JSX.Element[]> = {};
    interpolations.forEach((link) => {
        if (!linkMap[link.key]) {
            linkMap[link.key] = [];
        }
        linkMap[link.key].push(
            <Link key={`${link.key}-${linkMap[link.key].length}`} target="_blank" href={link.url}>
                {link.text}
            </Link>
        );
    });
    return linkMap;
};

/**
 * Extracts the key from a placeholder string
 * @param placeholder - String in format {{key}}
 * @returns The extracted key
 */
const extractKey = (placeholder: string): string => placeholder.replace(/^\{\{|\}\}$/g, '');

/**
 * Checks if a string is a placeholder
 * @param str - String to check
 * @returns True if string is a placeholder
 */
const isPlaceholder = (str: string): boolean => {
    const match = str.match(PLACEHOLDER_REGEX);
    return match !== null;
};

export const isFormattedCorrectly = (dynamicCopy: DynamicCopy | undefined): boolean => {
    if (!dynamicCopy) return false;
    const { text, interpolations } = dynamicCopy;
    return Boolean(text) && Array.isArray(interpolations);
};

const DynamicDisclosure = ({ dynamicCopy }: DynamicDisclosureProps): JSX.Element => {
    const { text, interpolations } = dynamicCopy;
    const linkMap = createLinkMap(interpolations);

    // Split text into alternating segments of regular text and placeholders
    const segments = text.split(new RegExp(`(${PLACEHOLDER_PATTERN.source})`, 'g'));
    
    // Convert each segment to either text or a Link component
    const renderedSegments = segments.map((segment, index) => {
        if (isPlaceholder(segment)) {
            const key = extractKey(segment);
            // Use the segment's index to determine which link to use
            const linkIndex = Math.floor(index / 2) % linkMap[key].length;
            return linkMap[key][linkIndex];
        }
        return segment;
    });

    return <>{renderedSegments}</>;
};

export default DynamicDisclosure; 